import { useMemo } from 'react'
import { useTasks } from '@/lib/db/rxdb/hooks/entities/tasks'
import { PROGRESS_TYPES } from '@/lib/types/subscription'

export interface AssigneeLoad {
  /** Open (not completed or cancelled) tasks per assignee user id, workspace-wide. */
  openCountByUser: Map<string, number>
  /** Assignees with open work in `projectId`: the picker's "suggested" people. */
  activeInProject: Set<string>
}

const CLOSED = new Set<string>([PROGRESS_TYPES.COMPLETED, PROGRESS_TYPES.CANCELLED])

/**
 * Who is carrying what, from the local task store (design 31b: the assignee picker
 * shows each person's open load so work can be spread). Subscribes only while
 * `enabled`, so a closed picker in every task row costs nothing.
 */
export function useAssigneeLoad(
  projectId: string | null | undefined,
  enabled: boolean,
): AssigneeLoad {
  const { tasks } = useTasks({ isArchived: false }, { enabled })

  return useMemo(() => {
    const openCountByUser = new Map<string, number>()
    const activeInProject = new Set<string>()
    for (const task of tasks) {
      if (!task.userId || task.isDeleted) continue
      if (CLOSED.has(task.status?.progressType.toUpperCase() ?? '')) continue
      openCountByUser.set(task.userId, (openCountByUser.get(task.userId) ?? 0) + 1)
      if (projectId && task.projectId === projectId) activeInProject.add(task.userId)
    }
    return { openCountByUser, activeInProject }
  }, [tasks, projectId])
}
